import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Trophy,
  Ticket,
  History,
  Plus,
  Trash2,
  Download,
  Gamepad2,
  Clock,
  Package,
  FileSpreadsheet,
  Ban,
  Pencil,
  BarChart3,
  Printer,
  Palette,
  Store,
  Mail,
  MessageCircle,
  MoreVertical,
  Eraser,
  Globe,
  Receipt,
  Dices,
  Upload,
  Sparkles,
  Loader2,
} from "lucide-react";
import LandingTab from "@/components/LandingTab";
import ReceiptOcrTab from "@/components/ReceiptOcrTab";
import FinalDrawTab from "@/components/FinalDrawTab";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PublishChecklist from "@/components/PublishChecklist";
import PrizeCalendar from "@/components/PrizeCalendar";
import { ImportDialog } from "@/components/ImportDialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import CreateContestDialog from "@/components/CreateContestDialog";
import { StatsGrid } from "@/components/StatsCharts";
import MediaGameTab from "@/components/MediaGameTab";
import StorePoolsTab from "@/components/StorePoolsTab";
import WhatsAppTab from "@/components/WhatsAppTab";
import CleanupDialog from "@/components/CleanupDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import AiStatsPanel from "@/components/AiStatsPanel";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" });

export default function ContestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contest, setContest] = useState(null);
  const [stats, setStats] = useState(null);
  const [editOpen, setEditOpen] = useState(new URLSearchParams(window.location.search).get("edit") === "1");
  const [cleanupOpen, setCleanupOpen] = useState(false);

  const loadContest = useCallback(async () => {
    setContest(await api.getContest(id));
    setStats(await api.stats(id));
  }, [id]);

  useEffect(() => {
    loadContest().catch(() => toast.error("Concorso non trovato"));
  }, [loadContest]);

  if (!contest) return <div className="p-10 text-muted-foreground">Caricamento…</div>;

  const sendSummary = async () => {
    if (!window.confirm("Inviare ora il riepilogo premi non ritirati ad admin e sedi?")) return;
    try {
      const r = await api.sendClosingSummary(id);
      toast.success(`Riepilogo inviato a ${r.sent.length} destinatari (${r.unredeemed} premi non ritirati)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3 lg:gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button data-testid="back-button" variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-xl truncate">{contest.name}</h1>
              <p className="text-xs text-muted-foreground">
                {fmt(contest.start_datetime)} → {fmt(contest.end_datetime)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="contest-header-actions">
            <Badge className="border border-primary/30 bg-primary/15 text-primary">{contest.status}</Badge>
            {contest.channel === "whatsapp" && <Badge data-testid="channel-badge" className="border border-emerald-500/30 bg-emerald-500/15 text-emerald-400 gap-1"><MessageCircle className="h-3 w-3" /> Solo WhatsApp</Badge>}
            <div className="sm:hidden ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="icon" data-testid="mobile-actions-menu"><MoreVertical className="h-5 w-5" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem data-testid="mobile-action-play" disabled={contest.channel === "whatsapp"} onClick={() => navigate(`/gioca/${id}`)} className="gap-2"><Gamepad2 className="h-4 w-4" /> Gioca</DropdownMenuItem>
                  <DropdownMenuItem data-testid="mobile-action-edit" onClick={() => setEditOpen(true)} className="gap-2"><Pencil className="h-4 w-4" /> Modifica</DropdownMenuItem>
                  <DropdownMenuItem data-testid="mobile-action-summary" onClick={sendSummary} className="gap-2"><Mail className="h-4 w-4" /> Invia riepilogo</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="mobile-action-cleanup" onClick={() => setCleanupOpen(true)} className="gap-2 text-red-400 focus:text-red-400"><Eraser className="h-4 w-4" /> Pulizia dati di prova</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <CleanupDialog contestId={id} onDone={loadContest} open={cleanupOpen} onOpenChange={setCleanupOpen} renderTrigger={() => null} />
            </div>
            <div className="hidden sm:contents">
            <CleanupDialog contestId={id} onDone={loadContest} />
            <Button
              data-testid="send-closing-summary-button"
              variant="outline"
              className="gap-2 border-border"
              title="Invia ora ad admin e sedi il riepilogo dei premi non ritirati"
              onClick={sendSummary}
            >
              <Mail className="h-4 w-4" /> Invia riepilogo
            </Button>
            <Button data-testid="edit-contest-button" variant="secondary" onClick={() => setEditOpen(true)} className="gap-2">
              <Pencil className="h-4 w-4" /> Modifica
            </Button>
            <Button data-testid="detail-play-button" onClick={() => navigate(`/gioca/${id}`)} className="gap-2" disabled={contest.channel === "whatsapp"} title={contest.channel === "whatsapp" ? "Concorso solo WhatsApp: il totem è disattivato" : ""}>
              <Gamepad2 className="h-4 w-4" /> Gioca
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <PublishChecklist contestId={contest.id} contestName={contest.name} refreshKey={JSON.stringify([contest.web_enabled, contest.rules_text, contest.logo_file_id, contest.start_datetime, contest.end_datetime])} />
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 sm:mb-8">
            <MiniStat label="Giocate totali" value={stats.total_plays} />
            <MiniStat label="Vincite totali" value={stats.total_wins} accent />
            <MiniStat label="Giocate oggi" value={stats.plays_today} />
            <MiniStat label="Codici usati" value={`${stats.codes_used}/${stats.codes_total}`} />
            {stats.rules && <MiniStat label="Regolamento letto" value={<span data-testid="rules-views-stat">{stats.rules.unique_views} <span className="text-xs text-muted-foreground font-normal">pers. · {stats.rules.views} aperture</span></span>} />}
            {stats.rules && <MiniStat label="Regolamento accettato" value={<span data-testid="rules-accepted-stat">{stats.rules.accepted} <span className="text-xs text-muted-foreground font-normal">({stats.rules.accepted_customers} registrati)</span></span>} />}
            {stats.landing && <MiniStat label="Landing visite" value={<span data-testid="landing-views-stat">{stats.landing.unique_views} <span className="text-xs text-muted-foreground font-normal">pers. · {stats.landing.views} visite</span></span>} />}
            {stats.landing && <MiniStat label="Landing clic" value={<span data-testid="landing-clicks-stat">{stats.landing.cta} <span className="text-xs text-muted-foreground font-normal">Partecipa ({stats.landing.cta_rate}%) · {stats.landing.login} Registrati</span></span>} />}
          </div>
        )}

        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "premi"}>
          <TabsList className="bg-secondary/60 h-auto flex-wrap justify-start gap-1 p-1" data-testid="contest-tabs">
            <TabsTrigger value="premi" data-testid="tab-premi" className="gap-2">
              <Trophy className="h-4 w-4" /> Premi
            </TabsTrigger>
            <TabsTrigger value="lotti" data-testid="tab-lotti" className="gap-2">
              <Ticket className="h-4 w-4" /> Lotti Codici
            </TabsTrigger>
            <TabsTrigger value="storico" data-testid="tab-storico" className="gap-2">
              <History className="h-4 w-4" /> Storico Giocate
            </TabsTrigger>
            <TabsTrigger value="statistiche" data-testid="tab-statistiche" className="gap-2">
              <BarChart3 className="h-4 w-4" /> Statistiche
            </TabsTrigger>
            <TabsTrigger value="grafica" data-testid="tab-grafica" className="gap-2">
              <Palette className="h-4 w-4" /> Gioco & Grafica
            </TabsTrigger>
            <TabsTrigger value="landing" data-testid="tab-landing" className="gap-2">
              <Globe className="h-4 w-4" /> Landing
            </TabsTrigger>
            <TabsTrigger value="sedi" data-testid="tab-sedi" className="gap-2">
              <Store className="h-4 w-4" /> Sedi & Montepremi
            </TabsTrigger>
            <TabsTrigger value="scontrini" data-testid="tab-scontrini" className="gap-2">
              <Receipt className="h-4 w-4" /> Scontrini OCR
            </TabsTrigger>
            <TabsTrigger value="estrazione" data-testid="tab-estrazione" className="gap-2">
              <Dices className="h-4 w-4" /> Estrazione finale
            </TabsTrigger>
            {contest.channel === "whatsapp" || contest.whatsapp_number ? (
              <TabsTrigger value="whatsapp" data-testid="tab-whatsapp" className="gap-2">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </TabsTrigger>
            ) : null}
          </TabsList>

          {(contest.channel === "whatsapp" || contest.whatsapp_number) && (
            <TabsContent value="whatsapp" className="mt-6">
              <WhatsAppTab contest={contest} onSaved={loadContest} />
            </TabsContent>
          )}

          <TabsContent value="premi" className="mt-6">
            <PrizesTab contestId={id} onChange={loadContest} />
            <div className="mt-6"><PrizeCalendar contestId={id} /></div>
          </TabsContent>
          <TabsContent value="scontrini" className="mt-6">
            <ReceiptOcrTab contest={contest} onSaved={loadContest} />
          </TabsContent>
          <TabsContent value="estrazione" className="mt-6">
            <FinalDrawTab contest={contest} />
          </TabsContent>
          <TabsContent value="lotti" className="mt-6">
            <BatchesTab contestId={id} />
          </TabsContent>
          <TabsContent value="storico" className="mt-6">
            <PlaysTab contestId={id} />
          </TabsContent>
          <TabsContent value="statistiche" className="mt-6">
            <StatsTab contestId={id} />
          </TabsContent>
          <TabsContent value="grafica" className="mt-6">
            <MediaGameTab contest={contest} onSaved={loadContest} />
          </TabsContent>
          <TabsContent value="landing" className="mt-6">
            <LandingTab contest={contest} />
          </TabsContent>
          <TabsContent value="sedi" className="mt-6">
            <StorePoolsTab contestId={id} />
          </TabsContent>
        </Tabs>
      </main>
      <CreateContestDialog open={editOpen} onOpenChange={setEditOpen} contest={contest} onCreated={loadContest} />
    </div>
  );
}

function StatsTab({ contestId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.statsDetail(contestId).then(setData);
  }, [contestId]);
  if (!data) return <p className="text-muted-foreground">Caricamento…</p>;
  return (
    <div data-testid="contest-stats">
      <StatsGrid data={data} exportUrl={api.exportRedemptionsUrl(contestId)} contestId={contestId} playsExportUrl={api.exportPlaysUrl(contestId)} />
      <AiStatsPanel contestId={contestId} />
    </div>
  );
}

const MiniStat = ({ label, value, accent }) => (
  <Card className="p-4 bg-card border-border">
    <div className={`font-display font-black text-2xl ${accent ? "text-amber-400" : ""}`}>{value}</div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
  </Card>
);

/* ---------------- Prizes ---------------- */
function PrizesTab({ contestId, onChange }) {
  const [prizes, setPrizes] = useState([]);
  const emptyForm = { name: "", tipologia: "", value: "", image_url: "", daily_quantity: 1, digital: false, digital_codes: "", digital_instructions: "", digital_low_threshold: 5 };
  const [form, setForm] = useState(emptyForm);
  const [slots, setSlots] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const regenImage = async (p) => {
    setRegenId(p.id);
    try {
      const r = await api.aiPrizeImage(contestId, { name: p.name, tipologia: p.tipologia, value: p.value });
      await api.updatePrize(p.id, { name: p.name, tipologia: p.tipologia || "Premio", value: p.value, image_url: r.url, daily_quantity: p.daily_quantity, time_slots: p.time_slots || [], digital: !!p.digital, digital_codes: p.digital_codes || [], digital_instructions: p.digital_instructions, digital_low_threshold: p.digital_low_threshold ?? 5 });
      await load(); onChange?.(); toast.success(`Nuova immagine per ${p.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generazione non riuscita"); }
    finally { setRegenId(null); }
  };
  const genAllImages = async () => {
    setBulkBusy(true);
    try {
      const r = await api.aiPrizeImagesBulk(contestId);
      await load(); onChange?.();
      if (r.failed.length) toast.warning(`${r.done.length} immagini generate, ${r.failed.length} non riuscite: ${r.failed.join(", ")}`);
      else toast.success(`${r.done.length} immagini generate${r.remaining ? ` · restano ${r.remaining}: premi di nuovo` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generazione non riuscita"); }
    finally { setBulkBusy(false); }
  };
  const genPrizeImage = async () => {
    setImgBusy(true);
    try { const r = await api.aiPrizeImage(contestId, { name: form.name, tipologia: form.tipologia, value: form.value }); setForm((f) => ({ ...f, image_url: r.url })); toast.success("Immagine generata: salva il premio per confermarla"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generazione non riuscita"); }
    finally { setImgBusy(false); }
  };

  const load = useCallback(async () => setPrizes(await api.listPrizes(contestId)), [contestId]);
  useEffect(() => {
    load();
  }, [load]);

  const addSlot = () => setSlots((s) => [...s, { _key: crypto.randomUUID(), start: "09:00", end: "13:00", quantity: 1 }]);
  const setSlot = (i, k, v) => setSlots((s) => s.map((sl, idx) => (idx === i ? { ...sl, [k]: v } : sl)));
  const rmSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, tipologia: p.tipologia || "", value: p.value || "", image_url: p.image_url || "", daily_quantity: p.daily_quantity, digital: !!p.digital, digital_codes: (p.digital_codes || []).join("\n"), digital_instructions: p.digital_instructions || "", digital_low_threshold: p.digital_low_threshold ?? 5 });
    setSlots(p.time_slots || []);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSlots([]);
  };

  const add = async () => {
    if (!form.name) return toast.error("Inserisci il nome del premio");
    const payload = {
      ...form,
      daily_quantity: Number(form.daily_quantity) || 1,
      digital: !!form.digital,
      digital_codes: form.digital ? String(form.digital_codes || "").split(/\r?\n|,|;/).map((s) => s.trim()).filter(Boolean) : [],
      digital_instructions: form.digital ? form.digital_instructions || null : null,
      digital_low_threshold: Number(form.digital_low_threshold) || 0,
      time_slots: slots.map(({ _key, ...s }) => ({ ...s, quantity: Number(s.quantity) || 1 })),
    };
    if (editingId) {
      await api.updatePrize(editingId, payload);
      toast.success("Premio aggiornato");
    } else {
      await api.addPrize(contestId, payload);
      toast.success("Premio aggiunto");
    }
    cancelEdit();
    load();
    onChange?.();
  };

  const remove = async (pid) => {
    await api.deletePrize(pid);
    toast.success("Premio eliminato");
    load();
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="lg:col-span-2 p-5 bg-card border-border h-fit">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">{editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {editingId ? "Modifica Premio" : "Aggiungi Premio"}</span>
          {!editingId && <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs font-normal" onClick={() => setImportOpen(true)} data-testid="prizes-import-button"><FileSpreadsheet className="h-3.5 w-3.5" /> Importa / Esporta Excel</Button>}
        </h3>
        <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { load(); onChange?.(); }} title="Importa premi da Excel / CSV" description="Scarica il modello, compila una riga per premio (nome, tipologia, valore, quantità al giorno, digitale e codici) e caricalo. I premi con lo stesso nome vengono aggiornati. Puoi anche esportare i premi attuali, correggerli e reimportarli." templateUrl={api.prizesImportTemplateUrl(contestId)} exportUrl={api.prizesExportUrl(contestId)} importFn={(f, dry) => api.prizesImport(contestId, f, dry)} entityLabel="premi" testPrefix="prizes-import" />
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome premio *</Label>
            <Input
              data-testid="prize-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Es. Buono Spesa 50€"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipologia</Label>
              <Input
                data-testid="prize-type-input"
                value={form.tipologia}
                onChange={(e) => setForm({ ...form, tipologia: e.target.value })}
                placeholder="Buono / Gadget…"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Valore</Label>
              <Input
                data-testid="prize-value-input"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="50€"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Immagine del premio (facoltativa)</Label>
            <div className="flex gap-2">
              <Input
                data-testid="prize-image-input"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://… oppure genera con l'IA"
              />
              <Button type="button" variant="outline" className="gap-1.5 shrink-0 border-violet-500/40 text-violet-600 hover:bg-violet-500/10" disabled={imgBusy || form.name.trim().length < 2} onClick={genPrizeImage} data-testid="prize-image-ai-button" title="Genera l'immagine dal nome del premio">
                {imgBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {imgBusy ? "Genero…" : "Genera con IA"}
              </Button>
            </div>
            {form.image_url && <img src={form.image_url} alt="" className="h-20 w-20 rounded-lg border border-border object-cover bg-white" data-testid="prize-image-preview" />}
            <p className="text-[11px] text-muted-foreground">L'immagine compare sui rulli della slot e nella lista premi.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Quantità giornaliera</Label>
            <Input
              data-testid="prize-quantity-input"
              type="number"
              min={1}
              value={form.daily_quantity}
              onChange={(e) => setForm({ ...form, daily_quantity: e.target.value })}
            />
          </div>

          <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${form.digital ? "border-emerald-500 bg-emerald-500/10" : "border-border"}`} data-testid="prize-digital-label">
            <input type="checkbox" checked={!!form.digital} onChange={(e) => setForm({ ...form, digital: e.target.checked })} className="mt-1 h-4 w-4 accent-emerald-600" data-testid="prize-digital-checkbox" />
            <span className="text-sm"><b>Premio digitale</b> (codice sconto / buono): consegnato subito via email e nella ricevuta, senza ritiro in negozio. La giocata risulta già "ritirata".</span>
          </label>
          {form.digital && (
            <div className="space-y-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-3">
              <div className="space-y-1.5">
                <Label>Codici da consegnare (uno per riga; ogni vincita ne consuma uno)</Label>
                <Textarea rows={4} value={form.digital_codes} onChange={(e) => setForm({ ...form, digital_codes: e.target.value })} placeholder={"SCONTO10-AB12\nSCONTO10-CD34"} className="font-mono-code text-xs" data-testid="prize-digital-codes" />
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-1 text-xs cursor-pointer rounded-md border border-border px-2 py-1.5 hover:bg-secondary"><Upload className="h-3.5 w-3.5" /> Carica da CSV / Excel
                    <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" data-testid="prize-digital-upload" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await api.parsePrizeCodes(contestId, f); const cur = String(form.digital_codes || "").split(/\r?\n/).filter(Boolean); setForm({ ...form, digital_codes: [...cur, ...r.codes.filter((c) => !cur.includes(c))].join("\n") }); toast.success(`${r.count} codici importati`); } catch { toast.error("File non leggibile"); } e.target.value = ""; }} />
                  </label>
                  <span className="text-[11px] text-muted-foreground">Un codice per riga o per cella; righe duplicate ignorate.</span>
                </div>
                <p className="text-[11px] text-muted-foreground">Codici disponibili: {String(form.digital_codes || "").split(/\r?\n|,|;/).filter((s) => s.trim()).length}. Se finiscono, il vincitore riceve comunque il premio con l'avviso che il codice gli sarà comunicato.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Avvisami via email quando restano</Label>
                <div className="flex items-center gap-2"><Input type="number" min={0} max={1000} value={form.digital_low_threshold ?? 5} onChange={(e) => setForm({ ...form, digital_low_threshold: Number(e.target.value) })} className="w-24" data-testid="prize-digital-threshold" /><span className="text-xs text-muted-foreground">codici (e comunque quando finiscono)</span></div>
              </div>
              <div className="space-y-1.5">
                <Label>Istruzioni per l'uso (facoltative)</Label>
                <Input value={form.digital_instructions} onChange={(e) => setForm({ ...form, digital_instructions: e.target.value })} placeholder="Es. Inserisci il codice nel carrello su www.sito.it entro 30 giorni" data-testid="prize-digital-instructions" />
              </div>
            </div>
          )}

          <div className="rounded-lg bg-secondary/40 p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Fasce orarie (facoltative)
              </Label>
              <Button data-testid="add-slot-button" size="sm" variant="ghost" onClick={addSlot}>
                <Plus className="h-3.5 w-3.5" /> Fascia
              </Button>
            </div>
            {slots.length === 0 && (
              <p className="text-xs text-muted-foreground">Nessuna fascia: si usa la quantità giornaliera.</p>
            )}
            <div className="space-y-2">
              {slots.map((s, i) => (
                <div key={s._key || `${s.start}-${s.end}-${i}`} className="flex items-center gap-2">
                  <Input type="time" value={s.start} onChange={(e) => setSlot(i, "start", e.target.value)} className="h-8" />
                  <Input type="time" value={s.end} onChange={(e) => setSlot(i, "end", e.target.value)} className="h-8" />
                  <Input
                    type="number"
                    min={1}
                    value={s.quantity}
                    onChange={(e) => setSlot(i, "quantity", e.target.value)}
                    className="h-8 w-16"
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmSlot(i)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Button data-testid="submit-prize-button" onClick={add} className="w-full gap-2">
            {editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {editingId ? "Salva Premio" : "Aggiungi Premio"}
          </Button>
          {editingId && (
            <Button data-testid="cancel-edit-prize-button" variant="ghost" onClick={cancelEdit} className="w-full">
              Annulla modifica
            </Button>
          )}
        </div>
      </Card>

      <div className="lg:col-span-3 space-y-3">
        {prizes.some((p) => !p.image_url) && (
          <Card className="p-3 flex flex-wrap items-center gap-3 border-violet-500/30 bg-violet-500/5" data-testid="prize-bulk-ai-card">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <p className="flex-1 text-xs text-muted-foreground min-w-[200px]"><b className="text-foreground">{prizes.filter((p) => !p.image_url).length} premi senza immagine.</b> Genera in blocco le foto prodotto con l'IA (circa 15 s per premio); compariranno sui rulli della slot e nella lista.</p>
            <Button size="sm" variant="outline" className="gap-1.5 border-violet-500/40 text-violet-600 hover:bg-violet-500/10" disabled={bulkBusy} onClick={genAllImages} data-testid="prize-bulk-ai-button">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {bulkBusy ? "Genero le immagini…" : "Genera immagini mancanti"}
            </Button>
          </Card>
        )}
        {prizes.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-2 bg-card/50">
            <Package className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <p className="text-muted-foreground mt-3">Nessun premio configurato. Aggiungine uno per abilitare le vincite.</p>
          </Card>
        ) : (
          prizes.map((p) => (
            <Card key={p.id} data-testid={`prize-row-${p.id}`} className="p-4 bg-card border-border flex items-center gap-4">
              <div className="h-14 w-14 rounded-xl bg-secondary grid place-items-center overflow-hidden shrink-0">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                ) : (
                  <Trophy className="h-6 w-6 text-amber-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold truncate">{p.name}</h4>
                  {p.value && <Badge variant="secondary">{p.value}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {p.tipologia || "Premio"}{p.digital ? ` · Digitale (${(p.digital_codes || []).length} codici)` : ""} · {p.time_slots?.length ? `${p.time_slots.length} fasce orarie` : `${p.daily_quantity}/giorno`}
                </p>
                <p className="text-xs text-emerald-400 mt-0.5">
                  Vinti oggi: {p.won_today} · Totali: {p.won_total}
                </p>
              </div>
              <Button
                data-testid={`regen-image-${p.id}`}
                variant="ghost"
                size="icon"
                disabled={regenId === p.id}
                onClick={() => regenImage(p)}
                title={p.image_url ? "Rigenera immagine con l'IA" : "Genera immagine con l'IA"}
                className="text-muted-foreground hover:text-violet-600"
              >
                {regenId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              </Button>
              <Button
                data-testid={`edit-prize-${p.id}`}
                variant="ghost"
                size="icon"
                onClick={() => startEdit(p)}
                className="text-muted-foreground hover:text-primary"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                data-testid={`delete-prize-${p.id}`}
                variant="ghost"
                size="icon"
                onClick={() => remove(p.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------------- Batches ---------------- */
function BatchesTab({ contestId }) {
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState(() => { const p = new URLSearchParams(window.location.search); return { store_name: "", prefix: "", quantity: 100, store_id: p.get("store") || "", label: "" }; });
  const [busy, setBusy] = useState(false);
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState(() => { const p = new URLSearchParams(window.location.search); return { q: "", store: p.get("store") || "", unusedOnly: false }; });

  const load = useCallback(async () => setBatches(await api.listBatches(contestId)), [contestId]);
  const q = filter.q.trim().toLowerCase();
  const filtered = batches.filter((b) => {
    if (filter.store && (b.store_id || b.store_name) !== filter.store) return false;
    if (filter.importedOnly && !b.imported) return false;
    if (filter.unusedOnly && (b.used_count || 0) >= b.quantity) return false;
    if (!q) return true;
    return [b.label, b.store_name, b.prefix].some((v) => (v || "").toLowerCase().includes(q));
  });
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const remaining = (b) => (b.quantity || 0) - (b.used_count || 0);
  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sort.key === "store") { va = (a.store_name || "").toLowerCase(); vb = (b.store_name || "").toLowerCase(); }
    else if (sort.key === "remaining") { va = remaining(a); vb = remaining(b); }
    else { va = a.created_at || ""; vb = b.created_at || ""; }
    const c = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === "asc" ? c : -c;
  });
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const [importOpen, setImportOpen] = useState(false);
  const storeOptions = [...new Map(batches.map((b) => [b.store_id || b.store_name, b.store_name])).entries()];
  useEffect(() => {
    load();
    api.listStores().then((ss) => { setStores(ss); const pre = ss.find((x) => x.id === form.store_id); if (pre && !form.store_name) setForm((f) => ({ ...f, store_name: pre.name })); });
  }, [load]);

  const create = async () => {
    if (!form.store_name) return toast.error("Inserisci il nome del punto vendita");
    setBusy(true);
    try {
      const res = await api.createBatch(contestId, { ...form, store_id: form.store_id || null, quantity: Number(form.quantity) || 100 });
      toast.success(`Lotto creato: ${res.generated} codici generati`);
      setForm({ store_name: "", prefix: "", quantity: 100, store_id: "" });
      load();
    } finally {
      setBusy(false);
    }
  };
  const [refill, setRefill] = useState(() => { const p = new URLSearchParams(window.location.search); const n = Number(p.get("refill")); return n > 0 && p.get("store") ? { qty: Math.min(n, 5000), storeId: p.get("store") } : null; });
  const refillStore = refill && stores.find((s) => s.id === refill.storeId);
  const doRefill = async () => {
    if (!refillStore) return;
    setBusy(true);
    try {
      const res = await api.createBatch(contestId, { store_name: refillStore.name, store_id: refillStore.id, prefix: "", quantity: refill.qty, label: `Rifornimento ${new Date().toLocaleDateString("it-IT")}` });
      toast.success(`Lotto di rifornimento creato: ${res.generated} codici per ${refillStore.name}`);
      setRefill(null); load();
      window.history.replaceState(null, "", window.location.pathname + "?tab=lotti");
    } catch (e) { toast.error(e?.response?.data?.detail || "Creazione non riuscita"); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      {refill && (
        <Card className="lg:col-span-5 p-4 border-amber-300 bg-amber-50 flex flex-wrap items-center justify-between gap-3" data-testid="batch-refill-banner">
          <div className="text-sm"><p className="font-semibold text-amber-900">Rifornimento con un clic</p><p className="text-amber-800 text-xs">{refillStore ? <>Genero un lotto da <b>{refill.qty}</b> codici per la sede <b>{refillStore.name}</b> (etichetta "Rifornimento {new Date().toLocaleDateString("it-IT")}").</> : "Caricamento sede…"}</p></div>
          <div className="flex gap-2"><Button variant="ghost" size="sm" onClick={() => setRefill(null)} data-testid="batch-refill-cancel">Annulla</Button><Button size="sm" disabled={busy || !refillStore} onClick={doRefill} className="gap-1.5" data-testid="batch-refill-confirm"><Ticket className="h-4 w-4" /> Conferma e genera {refill.qty} codici</Button></div>
        </Card>
      )}
      <Card className="lg:col-span-2 p-5 bg-card border-border h-fit">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Ticket className="h-4 w-4" /> Genera Lotto Codici</span>
          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs font-normal" onClick={() => setImportOpen(true)} data-testid="batches-import-button"><FileSpreadsheet className="h-3.5 w-3.5" /> Importa codici stampati</Button>
        </h3>
        <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} title="Importa codici già stampati" description="Hai già le cartoline o i volantini stampati? Carica l'Excel con i codici (uno per riga), la sede e il nome del lotto: vengono registrati così come sono, raggruppati in un lotto per sede. I codici già presenti o duplicati vengono scartati." templateUrl={api.batchesImportTemplateUrl(contestId)} importFn={(f, dry) => api.batchesImport(contestId, f, dry)} entityLabel="codici" testPrefix="batches-import" />
        <div className="space-y-3">
          {stores.length > 0 && (
            <div className="space-y-1.5">
              <Label>Sede registrata (per montepremi individuale)</Label>
              <select
                data-testid="batch-store-select"
                value={form.store_id}
                onChange={(e) => {
                  const s = stores.find((x) => x.id === e.target.value);
                  setForm({ ...form, store_id: e.target.value, store_name: s ? s.name : form.store_name });
                }}
                className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">Nessuna (montepremi generale)</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Nome lotto (facoltativo)</Label>
            <Input data-testid="batch-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Es. Volantino ottobre" />
          </div>
          <div className="space-y-1.5">
            <Label>Punto vendita / Sede *</Label>
            <Input
              data-testid="batch-store-input"
              value={form.store_name}
              onChange={(e) => setForm({ ...form, store_name: e.target.value })}
              placeholder="Es. Store Milano Centro"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Prefisso</Label>
              <Input
                data-testid="batch-prefix-input"
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                placeholder="MI"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Quantità</Label>
              <Input
                data-testid="batch-quantity-input"
                type="number"
                min={1}
                max={5000}
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </div>
          </div>
          <Button data-testid="generate-batch-button" onClick={create} disabled={busy} className="w-full gap-2">
            <Plus className="h-4 w-4" /> {busy ? "Generazione…" : "Genera Nuovo Lotto"}
          </Button>
        </div>
      </Card>

      <div className="lg:col-span-3 space-y-3">
        <CardInstructions contestId={contestId} />
        {batches.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center" data-testid="batch-filters">
            <Input data-testid="batch-filter-search" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Cerca per nome lotto, sede o prefisso…" className="h-9 flex-1" />
            <select data-testid="batch-filter-store" value={filter.store} onChange={(e) => setFilter({ ...filter, store: e.target.value })} className="h-9 rounded-md border border-border bg-background px-3 text-sm sm:w-56">
              <option value="">Tutte le sedi</option>
              {storeOptions.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer" data-testid="batch-filter-unused-row">
              <input type="checkbox" data-testid="batch-filter-unused" checked={filter.unusedOnly} onChange={(e) => setFilter({ ...filter, unusedOnly: e.target.checked })} className="h-4 w-4 accent-primary" />
              Solo con codici non usati
            </label>
            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="batch-filter-count">{filtered.length} / {batches.length} lotti</span>
            {sorted.length > 0 && (
              <a href={api.exportBatchesUrl(contestId, sorted.map((b) => b.id))} data-testid="export-filtered-batches">
                <Button size="sm" variant="secondary" className="h-9 gap-1.5"><Download className="h-3.5 w-3.5" /> Excel ({sorted.length})</Button>
              </a>
            )}
            {sorted.length > 0 && (
              <a href={api.batchesSummaryPdfUrl(contestId, sorted.map((b) => b.id))} target="_blank" rel="noreferrer" data-testid="batches-summary-pdf">
                <Button size="sm" variant="outline" className="h-9 gap-1.5 border-border" title="PDF con QR e istruzioni di distribuzione dei lotti visibili"><Printer className="h-3.5 w-3.5" /> PDF consegna</Button>
              </a>
            )}
            <a href={api.exportCodesByStoreUrl(contestId, batches.some((b) => b.imported) && filter.importedOnly, filter.store)} data-testid="export-codes-by-store" title="Excel con un foglio per sede: codice, lotto, usato/non usato, data utilizzo + riepilogo">
              <Button size="sm" variant="outline" className="h-9 gap-1.5 border-border"><Download className="h-3.5 w-3.5" /> Codici per sede</Button>
            </a>
            {batches.some((b) => b.imported) && (
              <label className="flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer" data-testid="batch-filter-imported-row">
                <input type="checkbox" data-testid="batch-filter-imported" checked={!!filter.importedOnly} onChange={(e) => setFilter({ ...filter, importedOnly: e.target.checked })} className="h-4 w-4 accent-primary" />
                Solo importati
              </label>
            )}
          </div>
        )}
        <BatchList batches={batches} filtered={sorted} sort={sort} onSort={toggleSort} />
      </div>
    </div>
  );
}

const SORT_COLS = [["created_at", "Data"], ["store", "Sede"], ["remaining", "Codici rimasti"]];

function SortHeader({ sort, onSort }) {
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground px-1" data-testid="batch-sort-header">
      <span className="mr-1">Ordina per:</span>
      {SORT_COLS.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onSort(k)} data-testid={`batch-sort-${k}`}
          className={`rounded-full px-2.5 py-1 border transition-colors ${sort.key === k ? "border-primary text-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
          {l}{sort.key === k && (sort.dir === "asc" ? " ↑" : " ↓")}
        </button>
      ))}
    </div>
  );
}

function BatchRow({ b }) {
  const remaining = (b.quantity || 0) - (b.used_count || 0);
  return (
    <Card data-testid={`batch-row-${b.id}`} className="p-4 bg-card border-border flex items-center gap-4">
      <div className="h-12 w-12 rounded-xl bg-secondary grid place-items-center shrink-0">
        <Package className="h-6 w-6 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold truncate">{b.label ? <>{b.label} <span className="text-muted-foreground font-normal">· {b.store_name}</span></> : b.store_name}{b.imported && <span className="ml-1.5 rounded-full border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 align-middle" data-testid={`batch-imported-${b.id}`}>importato</span>}</h4>
        <p className="text-xs text-muted-foreground">
          {b.quantity} codici {b.prefix ? `· prefisso ${b.prefix}` : ""} · usati {b.used_count} · <span className={remaining === 0 ? "text-red-400" : "text-emerald-400"}>rimasti {remaining}</span>
          {b.created_at && <> · {new Date(b.created_at).toLocaleDateString("it-IT")}</>}
        </p>
        {b.delivered_at && <p className="text-xs text-emerald-400" data-testid={`batch-delivered-${b.id}`}>✓ Ricezione confermata il {new Date(b.delivered_at).toLocaleDateString("it-IT")} da {b.delivered_by} ({b.delivered_store_name})</p>}
      </div>
      <a href={api.printBatchPdfUrl(b.id)} target="_blank" rel="noreferrer" data-testid={`print-batch-${b.id}`}>
        <Button variant="outline" className="gap-2 border-border">
          <Printer className="h-4 w-4" /> Stampa PDF
        </Button>
      </a>
      <a href={api.exportBatchUrl(b.id)} data-testid={`export-batch-${b.id}`}>
        <Button variant="secondary" className="gap-2">
          <Download className="h-4 w-4" /> Excel
        </Button>
      </a>
    </Card>
  );
}

function BatchList({ batches, filtered, sort, onSort }) {
  if (batches.length === 0) {
    return (
      <Card className="p-10 text-center border-dashed border-2 bg-card/50">
        <Ticket className="h-10 w-10 mx-auto text-muted-foreground/40" />
        <p className="text-muted-foreground mt-3">Nessun lotto generato. Crea un lotto da distribuire ai punti vendita.</p>
      </Card>
    );
  }
  if (filtered.length === 0) {
    return (
      <Card className="p-6 text-center border-dashed bg-card/50" data-testid="batch-filter-empty">
        <p className="text-sm text-muted-foreground">Nessun lotto corrisponde ai filtri.</p>
      </Card>
    );
  }
  return (
    <>
      <SortHeader sort={sort} onSort={onSort} />
      {filtered.map((b) => <BatchRow key={b.id} b={b} />)}
    </>
  );
}

/* ---------------- Card instructions ---------------- */
function CardInstructions({ contestId }) {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  useEffect(() => {
    api.getContest(contestId).then((c) => {
      setText(c.card_instructions || "");
      setSaved(c.card_instructions || "");
    });
  }, [contestId]);
  const save = async () => {
    const r = await api.setCardInstructions(contestId, text);
    setText(r.card_instructions || "");
    setSaved(r.card_instructions || "");
    toast.success("Istruzioni salvate: verranno stampate sulle card dei codici");
  };
  return (
    <Card className="p-4 bg-card border-border" data-testid="card-instructions">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Label className="flex items-center gap-2"><Printer className="h-3.5 w-3.5" /> Istruzioni sulle card codici (PDF)</Label>
          <textarea
            data-testid="card-instructions-input"
            value={text}
            onChange={(e) => setText(e.target.value.split("\n").slice(0, 4).join("\n"))}
            rows={4}
            maxLength={200}
            placeholder={"Vuoto = testo predefinito:\n1. Avvicinati al totem Vivi Win\n2. Inquadra il QR o digita il codice\n3. Gioca e scopri subito se hai vinto!"}
            className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono-code resize-none"
          />
          <p className="text-[11px] text-muted-foreground">Massimo 4 righe, 46 caratteri per riga.</p>
        </div>
        <Button data-testid="card-instructions-save" size="sm" onClick={save} disabled={text === saved} className="mt-6">Salva</Button>
      </div>
    </Card>
  );
}

/* ---------------- Plays ---------------- */
function PlayStateCell({ p }) {
  if (p.result !== "win") return "—";
  if (p.redeemed) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Badge className="bg-slate-500/15 text-slate-300 border border-slate-500/30">Ritirato</Badge>
        <a href={api.pickupReceiptPdfUrl(p.id)} target="_blank" rel="noopener noreferrer" data-testid={`pickup-pdf-${p.id}`} title="Stampa ricevuta di ritiro" className="text-muted-foreground hover:text-primary">
          <Printer className="h-4 w-4" />
        </a>
      </span>
    );
  }
  if (p.expired) return <Badge className="bg-destructive/15 text-destructive border border-destructive/30">Scaduta</Badge>;
  return <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30">Da ritirare</Badge>;
}

function PlaysTab({ contestId }) {
  const [plays, setPlays] = useState([]);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => setPlays(await api.listPlays(contestId, filter)), [contestId, filter]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2">
          {[
            { k: "", l: "Tutte" },
            { k: "win", l: "Vinte" },
            { k: "lose", l: "Perse" },
          ].map((f) => (
            <Button
              key={f.k}
              data-testid={`filter-${f.k || "all"}`}
              size="sm"
              variant={filter === f.k ? "default" : "secondary"}
              onClick={() => setFilter(f.k)}
            >
              {f.l}
            </Button>
          ))}
        </div>
        <a href={api.exportPlaysUrl(contestId)} data-testid="export-plays-button">
          <Button variant="secondary" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Esporta Excel
          </Button>
        </a>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Data/Ora</th>
                <th className="px-4 py-3 font-semibold">Codice</th>
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Punto Vendita</th>
                <th className="px-4 py-3 font-semibold">Esito</th>
                <th className="px-4 py-3 font-semibold">Premio</th>
                <th className="px-4 py-3 font-semibold">Seriale</th>
                <th className="px-4 py-3 font-semibold">Ritiro</th>
                <th className="px-4 py-3 font-semibold">Consenso</th>
              </tr>
            </thead>
            <tbody>
              {plays.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                    Nessuna giocata registrata.
                  </td>
                </tr>
              ) : (
                plays.map((p) => (
                  <tr key={p.id} data-testid={`play-row-${p.id}`} className="border-t border-border">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(p.created_at)}</td>
                    <td className="px-4 py-3 font-mono-code">{p.code}
                      {p.code_type === "carta_fedeltà" && <button type="button" title="Blocca questa carta" onClick={async () => { if (!window.confirm(`Bloccare la carta ${p.code}? Non potrà più giocare in questo concorso.`)) return; await api.loyaltyBlock(contestId, p.code, "Bloccata dallo storico giocate"); toast.success("Carta bloccata"); }} className="ml-1.5 inline-flex align-middle text-muted-foreground hover:text-red-500" data-testid={`play-block-card-${p.id}`}><Ban className="h-3.5 w-3.5" /></button>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{p.code_type}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.store_name || "—"}</td>
                    <td className="px-4 py-3">
                      {p.result === "win" ? (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">VINTA</Badge>
                      ) : (
                        <Badge className="bg-slate-500/15 text-slate-400 border border-slate-500/30">PERSA</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">{p.prize_name || "—"}</td>
                    <td className="px-4 py-3 font-mono-code text-xs">{p.receipt_serial || "—"}</td>
                    <td className="px-4 py-3"><PlayStateCell p={p} /></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap" data-testid={`play-consent-${p.id}`}>
                      {p.rules_accepted_at ? <><span className="text-foreground">Reg. v{p.rules_version || 1}{p.privacy_version ? ` · Privacy v${p.privacy_version}` : ""}</span><br />{fmt(p.rules_accepted_at)}</> : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
